// apiUtils.js

export function cleanString(value) {
  return String(value || '').trim();
}

export const NON_API_FIELD_TYPES = Object.freeze([
  'cover',
]);

export const NON_QUERYABLE_FIELD_TYPES = Object.freeze([
  'activity',
  'comments',
  'cover',
]);

export function isQueryableFieldType(fieldTypeValue) {
  const normalizedType = cleanString(fieldTypeValue).toLowerCase();
  if (!normalizedType) return true;
  return !NON_QUERYABLE_FIELD_TYPES.includes(normalizedType);
}

export function resolveBaseUrl() {
  const configured = cleanString(
    process.env.REACT_APP_BASE_API_BASE_URL
    || process.env.REACT_APP_WORKSPACE_API_BASE_URL,
  );
  const origin = typeof window !== 'undefined' ? cleanString(window.location?.origin) : '';
  return (configured || origin).replace(/\/+$/, '');
}

export function downloadFile(fileName, contents, contentType) {
  const blob = new Blob([contents], { type: contentType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

export function formatTimestamp(value) {
  const raw = cleanString(value);
  if (!raw) return 'n/a';

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString();
}

function pickFirstObject(candidates) {
  for (const candidate of candidates) {
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
      return candidate;
    }
  }
  return null;
}

export function resolveOpenApiDocument(payload) {
  const root = pickFirstObject([payload]);
  if (!root) return null;

  const candidates = [
    root,
    root.spec,
    root.openapiSpec,
    root.openapiDocument,
    root.openapi,
    root.data,
    root.data?.spec,
    root.data?.openapi,
    root.payload,
  ];

  const doc = candidates.find((candidate) => {
    return candidate
      && typeof candidate === 'object'
      && !Array.isArray(candidate)
      && candidate.paths
      && typeof candidate.paths === 'object'
      && !Array.isArray(candidate.paths);
  });

  return doc || root;
}

export function resolveOpenApiGeneratedAt(payload, openapiDoc) {
  return cleanString(
    openapiDoc?.['x-generated-at']
    || openapiDoc?.generatedAt
    || openapiDoc?.generated_at
    || payload?.['x-generated-at']
    || payload?.generatedAt
    || payload?.generated_at
    || payload?.meta?.generatedAt
    || payload?.data?.generatedAt,
  );
}

export function stripBasePrefix(pathValue) {
  const rawPath = cleanString(pathValue);
  if (!rawPath) return '';

  const basePrefixPattern = /^\/v1\/bases\/[^/]+/;
  if (basePrefixPattern.test(rawPath)) {
    return rawPath.replace(basePrefixPattern, '') || '/';
  }

  return rawPath;
}

export function isIntegratorAllowedMethod(pathValue, methodValue) {
  const pathWithoutBase = stripBasePrefix(pathValue);
  const method = cleanString(methodValue).toUpperCase();

  if (!pathWithoutBase || !method) return false;

  const nodePaths = new Set(['/nodes', '/nodes/query', '/nodes/{nodeId}']);
  const typePaths = new Set(['/types', '/types/{typeId}', '/types/{typeId}/fields']);

  if (nodePaths.has(pathWithoutBase)) {
    if (pathWithoutBase === '/nodes') return method === 'GET' || method === 'POST';
    if (pathWithoutBase === '/nodes/query') return method === 'POST';
    if (pathWithoutBase === '/nodes/{nodeId}') return method === 'GET' || method === 'PATCH' || method === 'DELETE';
  }

  if (typePaths.has(pathWithoutBase)) {
    return method === 'GET';
  }

  return false;
}

function isFalseLike(value) {
  if (value === false || value === 0) return true;
  const normalized = cleanString(value).toLowerCase();
  return normalized === 'false' || normalized === '0' || normalized === 'no';
}

function isTrueLike(value) {
  if (value === true || value === 1) return true;
  const normalized = cleanString(value).toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
}

function isContractTestTypeLabel(labelValue) {
  const label = cleanString(labelValue).toLowerCase();
  if (!label) return false;

  return (
    label.startsWith('owner permission type ')
    || label.startsWith('admin permission type ')
    || label.startsWith('member forbidden type ')
    || label.startsWith('sa forbidden type ')
  );
}

function resolveOptionLabelFromTranslations(translations) {
  if (!translations || typeof translations !== 'object' || Array.isArray(translations)) return '';

  const englishLabel = cleanString(translations?.en?.label || translations?.en);
  if (englishLabel) return englishLabel;

  const translationRows = Object.values(translations);
  for (const row of translationRows) {
    const label = cleanString(row?.label || row);
    if (label) return label;
  }

  return '';
}

function normalizeFieldOptions(fieldRow) {
  const optionSources = [
    fieldRow?.options,
    fieldRow?.enumOptions,
    fieldRow?.allowedValues,
    fieldRow?.values,
    fieldRow?.choices,
  ];

  const rawArray = optionSources.find((source) => Array.isArray(source));
  let rows = Array.isArray(rawArray) ? rawArray : [];

  if (rows.length === 0) {
    const rawMap = optionSources.find((source) => source && typeof source === 'object' && !Array.isArray(source));
    if (rawMap) {
      rows = Object.entries(rawMap).map(([key, value]) => ({
        value: key,
        label: value,
      }));
    }
  }

  const seenValues = new Set();
  const normalized = [];

  rows.forEach((optionRow) => {
    const optionIsObject = optionRow && typeof optionRow === 'object' && !Array.isArray(optionRow);
    const value = cleanString(
      optionIsObject
        ? optionRow.value || optionRow.id || optionRow.key || optionRow.name || optionRow.label
        : optionRow,
    );
    if (!value || seenValues.has(value)) return;

    const label = cleanString(
      optionIsObject
        ? optionRow.label
          || optionRow.title
          || optionRow.name
          || resolveOptionLabelFromTranslations(optionRow.translations)
        : optionRow,
    ) || value;

    seenValues.add(value);
    normalized.push({
      value,
      label,
    });
  });

  return normalized;
}

function isApiVisibleFieldType(fieldTypeValue) {
  const normalizedType = cleanString(fieldTypeValue).toLowerCase();
  if (!normalizedType) return true;
  return !NON_API_FIELD_TYPES.includes(normalizedType);
}

export function normalizeTypeCatalogRows(rawRows) {
  const normalizedRows = Array.isArray(rawRows) ? rawRows : [];
  const deduped = new Map();

  normalizedRows.forEach((row) => {
    const resolvedTypeId = cleanString(
      row?.typeId
      || row?.id
      || row?.key
      || row?.slug
      || row?.type,
    );
    const resolvedLabel = cleanString(row?.label || row?.name || row?.title) || '';

    const isSystemType = row?.system === true || String(row?.system || '').toLowerCase() === 'true';
    const isHiddenFromSelector = isFalseLike(row?.showInSelector);
    const isDisabled = isFalseLike(row?.enabled);
    const isInternalType = isTrueLike(row?.internal) || isTrueLike(row?.internalOnly);
    const isKnownContractTestType = isContractTestTypeLabel(resolvedLabel);
    if (isSystemType || isHiddenFromSelector || isDisabled || isInternalType || isKnownContractTestType) return;

    if (!resolvedTypeId) return;

    const fieldRows = Array.isArray(row?.fields)
      ? row.fields
      : Array.isArray(row?.schema?.fields)
        ? row.schema.fields
        : Array.isArray(row?.attributes)
          ? row.attributes
          : [];

    const fields = fieldRows
      .map((fieldRow) => ({
        fieldId: cleanString(fieldRow?.fieldId || fieldRow?.id || fieldRow?.key || fieldRow?.name),
        slug: cleanString(fieldRow?.slug || fieldRow?.apiName),
        label: cleanString(fieldRow?.label || fieldRow?.name || fieldRow?.title),
        type: cleanString(fieldRow?.type || fieldRow?.dataType || fieldRow?.kind),
        options: normalizeFieldOptions(fieldRow),
      }))
      .filter((fieldRow) => fieldRow.fieldId && isApiVisibleFieldType(fieldRow.type));

    const nextRow = {
      typeId: resolvedTypeId,
      label: resolvedLabel,
      iconKey: cleanString(row?.iconKey || row?.icon || row?.iconName),
      fields,
    };

    const existing = deduped.get(resolvedTypeId);
    if (!existing) {
      deduped.set(resolvedTypeId, nextRow);
      return;
    }

    if (existing.fields.length === 0 && nextRow.fields.length > 0) {
      deduped.set(resolvedTypeId, nextRow);
      return;
    }

    if (!existing.iconKey && nextRow.iconKey) {
      deduped.set(resolvedTypeId, nextRow);
    }
  });

  return Array.from(deduped.values()).sort((a, b) => a.label.localeCompare(b.label));
}

export function extractTypeRowsFromSchemaDoc(schemaDoc) {
  const candidateArrays = [
    schemaDoc?.types,
    schemaDoc?.nodeTypes,
    schemaDoc?.typeDefinitions,
    schemaDoc?.schema?.types,
  ];

  let rows = candidateArrays.find((value) => Array.isArray(value));

  if (!rows) {
    const typeMapCandidate =
      (schemaDoc?.types && typeof schemaDoc.types === 'object' && !Array.isArray(schemaDoc.types) ? schemaDoc.types : null)
      || (schemaDoc?.nodeTypes && typeof schemaDoc.nodeTypes === 'object' && !Array.isArray(schemaDoc.nodeTypes) ? schemaDoc.nodeTypes : null);

    if (typeMapCandidate) {
      rows = Object.entries(typeMapCandidate).map(([key, value]) => ({
        ...(value && typeof value === 'object' ? value : {}),
        typeId: cleanString(value?.typeId || value?.id || value?.key) || key,
      }));
    }
  }

  return normalizeTypeCatalogRows(rows);
}

export function mapSchemaForDownload(value) {
  if (Array.isArray(value)) {
    return value.map((row) => mapSchemaForDownload(row));
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  const mappedObject = Object.entries(value).reduce((accumulator, [entryKey, entryValue]) => {
    accumulator[entryKey] = mapSchemaForDownload(entryValue);
    return accumulator;
  }, {});

  const fieldId = cleanString(value.fieldId);
  const fieldName = cleanString(value.name);

  if (fieldId && fieldId === fieldName) {
    const { fieldId: _fieldId, name: _name, ...rest } = mappedObject;
    return {
      key: fieldId,
      ...rest,
    };
  }

  return mappedObject;
}
